import React from 'react';
import { Card, Avatar, Tag, Button, Tooltip, Progress } from 'antd';
import {
  TrophyOutlined,
  UserOutlined,
  TeamOutlined,
  StarOutlined,
  EyeOutlined,
  CrownOutlined,
  VideoCameraOutlined,
  RiseOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './StudyProgramLeaderboardCard.css';

const StudyProgramLeaderboardCard = ({ rank, studyProgram, isTopThree }) => {
  const navigate = useNavigate();

  const getProdiCode = (studyProgram) => {
    // Use the code from backend if available (e.g., "IF" for Informatika)
    if (studyProgram.code && typeof studyProgram.code === 'string') {
      // Return the code, max 3 characters for display
      const code = studyProgram.code.toUpperCase().replace(/^PS-T?/, '').substring(0, 3);
      // If code looks like a proper abbreviation (2-4 letters), use it
      if (/^[A-Z]{2,4}$/.test(code)) {
        return code.substring(0, 2);
      }
    }

    // Fallback: Map common program names to their standard codes
    if (studyProgram.name) {
      const nameLower = studyProgram.name.toLowerCase();

      // Standard ITERA program code mappings
      const codeMap = {
        'informatika': 'IF',
        'elektro': 'EL',
        'sipil': 'SI',
        'mesin': 'MS',
        'kimia': 'TK',
        'fisika': 'FI',
        'biologi': 'BI',
        'matematika': 'MA',
        'geologi': 'GL',
        'geofisika': 'TG',
        'geomatika': 'GT',
        'arsitektur': 'AR',
        'lingkungan': 'TL',
        'industri': 'TI',
        'material': 'MT',
        'farmasi': 'FA',
        'pangan': 'TP',
        'pertambangan': 'TA',
        'kelautan': 'KL',
        'biosistem': 'TBS',
        'biomedis': 'BM',
        'telekomunikasi': 'TT',
        'perencanaan': 'PWK',
        'lanskap': 'ARL',
        'aktuaria': 'AT',
        'komunikasi visual': 'DKV',
        'perkeretaapian': 'KA',
        'data': 'SD',
        'atmosfer': 'SAP',
        'kosmetik': 'KOS',
        'pariwisata': 'PAR',
        'energi': 'TSE',
        'kehutanan': 'RH',
        'automasi': 'IA',
        'instrumentasi': 'IA',
        'minyak': 'RMG',
        'keolahragaan': 'RO',
        'tata kelola air': 'TKA',
      };

      for (const [key, code] of Object.entries(codeMap)) {
        if (nameLower.includes(key)) {
          return code;
        }
      }

      // Final fallback: first 2 letters of the main word
      const commonWords = ['program', 'studi', 'teknik', 'sains', 'dan', 'ilmu', 'rekayasa', 'teknologi'];
      const words = nameLower.split(/\s+/).filter(word => !commonWords.includes(word));

      if (words.length > 0) {
        return words[words.length - 1].substring(0, 2).toUpperCase();
      }
    }

    return '??';
  };

  const getRankIcon = () => {
    if (rank === 1) return <CrownOutlined className="rank-icon gold" />;
    if (rank === 2) return <TrophyOutlined className="rank-icon silver" />;
    if (rank === 3) return <TrophyOutlined className="rank-icon bronze" />;
    return null;
  };

  const getRankBadge = () => {
    if (rank === 1) return { text: 'Gold', class: 'badge-gold' };
    if (rank === 2) return { text: 'Silver', class: 'badge-silver' };
    if (rank === 3) return { text: 'Bronze', class: 'badge-bronze' };
    return { text: 'default', class: '' };
  };

  const getRankClass = () => {
    if (rank === 1) return 'rank-1';
    if (rank === 2) return 'rank-2';
    if (rank === 3) return 'rank-3';
    return '';
  };

  const getTotalContributors = () => {
    return (
      (studyProgram.dosen_count || 0) +
      (studyProgram.mahasiswa_count || 0) +
      (studyProgram.tendik_count || 0)
    );
  };

  const getContributionPercentage = (count, total) => {
    return total > 0 ? Math.round((count / total) * 100) : 0;
  };

  const handleViewDetails = () => {
    navigate(`/study-program/${studyProgram.code.toLowerCase()}`);
  };

  if (isTopThree) {
    return (
      <Card className={`study-program-card podium-card ${getRankClass()}`}>
        <div className="podium-rank">
          {getRankIcon()}
          <span className="rank-number">#{rank}</span>
        </div>

        <div className="prodi-avatar-container">
          <Avatar
            size={100}
            className="prodi-avatar"
            style={{
              backgroundColor: studyProgram.color || '#14B8A6',
              fontSize: '36px',
              fontWeight: 700,
            }}
          >
            {getProdiCode(studyProgram)}
          </Avatar>
          {rank <= 3 && (
            <div className={`podium-badge ${getRankBadge(rank).class}`}>
              {getRankBadge(rank).text}
            </div>
          )}
        </div>

        <div className="prodi-info">
          <h3 className="prodi-name">{studyProgram.name.replace('Program Studi', 'Prodi')}</h3>
          <div className="prodi-faculty-tags">
            <Tag color="purple" className="prodi-faculty">
              {studyProgram.faculty}
            </Tag>
          </div>
          {studyProgram.description && (
            <p className="prodi-description">
              {studyProgram.description}
            </p>
          )}
        </div>

        <div className="total-stats">
          <div className="total-stat-item">
            <StarOutlined className="total-stat-icon" />
            <div className="total-stat-info">
              <div className="total-stat-value">{studyProgram.total_points}</div>
              <div className="total-stat-label">Total Poin</div>
            </div>
          </div>
          <div className="stat-divider"></div>
          <div className="total-stat-item">
            <VideoCameraOutlined className="total-stat-icon" />
            <div className="total-stat-info">
              <div className="total-stat-value">{studyProgram.total_videos}</div>
              <div className="total-stat-label">Total Video</div>
            </div>
          </div>
        </div>

        {studyProgram.participation_percentage !== undefined && (
          <div className="participation-container">
            <Tooltip title="Persentase anggota yang berkontribusi">
              <div className="participation-box">
                <div className="participation-label-container">
                  <RiseOutlined className="participation-icon" />
                  <span className="participation-label">
                    Partisipasi
                  </span>
                </div>
                <span className="participation-value">
                  {studyProgram.participation_percentage.toFixed(1)}%
                </span>
              </div>
            </Tooltip>
          </div>
        )}

        <div className="contributors-section">
          <h4 className="section-title">
            <TeamOutlined /> Kontributor ({getTotalContributors()} Orang)
          </h4>
          
          <div className="contributor-stats">
            <div className="contributor-item">
              <div className="contributor-header">
                <UserOutlined className="contributor-icon dosen" />
                <span className="contributor-label">Dosen</span>
              </div>
              <div className="contributor-count">{studyProgram.dosen_count || 0}</div>
              <Progress
                percent={getContributionPercentage(
                  studyProgram.dosen_count || 0,
                  getTotalContributors()
                )}
                strokeColor="#14B8A6"
                size="small"
                showInfo={false}
              />
            </div>

            <div className="contributor-item">
              <div className="contributor-header">
                <UserOutlined className="contributor-icon mahasiswa" />
                <span className="contributor-label">Mahasiswa</span>
              </div>
              <div className="contributor-count">{studyProgram.mahasiswa_count || 0}</div>
              <Progress
                percent={getContributionPercentage(
                  studyProgram.mahasiswa_count || 0,
                  getTotalContributors()
                )}
                strokeColor="#14B8A6"
                size="small"
                showInfo={false}
              />
            </div>

            <div className="contributor-item">
              <div className="contributor-header">
                <UserOutlined className="contributor-icon tendik" />
                <span className="contributor-label">Tendik</span>
              </div>
              <div className="contributor-count">{studyProgram.tendik_count || 0}</div>
              <Progress
                percent={getContributionPercentage(
                  studyProgram.tendik_count || 0,
                  getTotalContributors()
                )}
                strokeColor="#faad14"
                size="small"
                showInfo={false}
              />
            </div>
          </div>
        </div>

        <Tooltip title="Lihat detail kontribusi program studi">
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={handleViewDetails}
            className="view-details-btn"
            block
          >
            Lihat Detail
          </Button>
        </Tooltip>
      </Card>
    );
  }

  return (
    <Card className="study-program-card list-card" hoverable>
      <div className="list-card-content">
        <div className="left-section">
          <div className="rank-badge">
            <span className="rank-number">#{rank}</span>
          </div>

          <Avatar
            size={60}
            className="prodi-avatar-small"
            style={{
              backgroundColor: studyProgram.color || '#14B8A6',
              fontSize: '20px',
              fontWeight: 700,
            }}
          >
            {getProdiCode(studyProgram)}
          </Avatar>

          <div className="prodi-info-compact">
            <h4 className="prodi-name-compact">{studyProgram.name.replace('Program Studi', 'Prodi')}</h4>
            <div className="prodi-faculty-tags-compact">
              <Tag color="purple" className="prodi-faculty-compact">
                {studyProgram.faculty}
              </Tag>
            </div>
            {studyProgram.description && (
              <p className="prodi-description-compact">
                {studyProgram.description}
              </p>
            )}
          </div>
        </div>

        <div className="middle-section">
          <div className="stats-compact">
            <Tooltip title="Total Poin">
              <div className="stat-compact">
                <StarOutlined className="stat-icon-compact" />
                <span className="stat-value-compact">{studyProgram.total_points}</span>
              </div>
            </Tooltip>

            <Tooltip title="Total Video">
              <div className="stat-compact">
                <VideoCameraOutlined className="stat-icon-compact" />
                <span className="stat-value-compact">{studyProgram.total_videos}</span>
              </div>
            </Tooltip>

            {studyProgram.participation_percentage !== undefined && (
              <Tooltip title="Tingkat Partisipasi">
                <div className="stat-compact">
                  <RiseOutlined className="stat-icon-compact stat-icon-participation" />
                  <span className="stat-value-compact stat-value-participation">
                    {studyProgram.participation_percentage.toFixed(1)}%
                  </span>
                </div>
              </Tooltip>
            )}
          </div>

          <div className="contributors-compact">
            <Tooltip title="Dosen">
              <div className="contributor-compact dosen">
                <UserOutlined />
                <span>{studyProgram.dosen_count || 0}</span>
              </div>
            </Tooltip>

            <Tooltip title="Mahasiswa">
              <div className="contributor-compact mahasiswa">
                <UserOutlined />
                <span>{studyProgram.mahasiswa_count || 0}</span>
              </div>
            </Tooltip>

            <Tooltip title="Tendik">
              <div className="contributor-compact tendik">
                <UserOutlined />
                <span>{studyProgram.tendik_count || 0}</span>
              </div>
            </Tooltip>
          </div>
        </div>

        <div className="right-section">
          <div className="total-contributors-badge">
            <TeamOutlined />
            <span>{getTotalContributors()}</span>
            <small>Total</small>
          </div>

          <Tooltip title="Lihat detail">
            <Button
              type="primary"
              icon={<EyeOutlined />}
              onClick={handleViewDetails}
              className="view-btn-compact"
            >
              Detail
            </Button>
          </Tooltip>
        </div>
      </div>
    </Card>
  );
};

export default StudyProgramLeaderboardCard;